<template>
  <div class="medical-report-page">
    <portal to="page-name">Laudo</portal>
    <template v-if="!loading">
      <report-header
        :data="form"
        :can-edit="canEdit"
        :is-external="isExternal"
        @change-template="changeTemplate"
        ref="header"
      ></report-header>

      <div class="medical-report-content">
        <report-content
          :data="form"
          :can-edit="canEdit"
          :is-external="isExternal"
          @save="save(false, false)"
          ref="content"
        ></report-content>
        <report-sidebar
          :data="form"
          :can-edit="canEdit"
          :can-re-sign="canReSign"
          :is-external="isExternal"
          :is-finished="isFinished"
          :saving="saving"
          @save="save(false, false)"
          @sign="sign"
          @resign="resign"
          @change-mask="changeMask"
          @external-report="setExternalReport"
        ></report-sidebar>
      </div>
    </template>

    <dx-modal
      title="Finalizar laudo"
      v-model="finishModal.show"
      id="medical-report-modal-finish"
      size="md"
    >
      <div class="form-group">
        <h6 class="text-info">Profissional</h6>
        <div class="divider"></div>

        <div class="tile">
          <div class="tile-icon">
            <div class="avatar avatar-lg avatar-gray">
              <fa-icon :icon="['fal', 'user-md']" />
            </div>
          </div>
          <div class="tile-content">
            <div :class="{'has-error': $v.finishModal.form.professionalId.$error}">
              <div class="tile-title">Profissional responsável pelo laudo</div>
              <select
                id="professional"
                class="form-select auto-width"
                :class="{ readonly: !finishModal.changeProfessional }"
                @blur="$v.finishModal.form.professionalId.$touch()"
                @change="finishModal.changeProfessional = false"
                v-model="finishModal.form.professionalId"
                :disabled="!finishModal.changeProfessional"
              >
                <option value="">Selecione um profissional</option>
                <option
                  v-for="(item, i) in finishModal.professionals"
                  :value="item.id"
                  :key="i"
                >{{ item.name }}</option>
              </select>
              <template v-if="$v.finishModal.form.professionalId.$error">
                <div
                  class="form-input-hint"
                  v-if="!$v.finishModal.form.professionalId.required"
                >Campo obrigatório</div>
              </template>
            </div>
          </div>
          <div class="tile-action text-right">
            <button
              class="btn btn-icon btn-info-outline tooltip tooltip-left"
              @click="finishModal.changeProfessional = true"
              data-tooltip="Trocar profissional"
              :disabled="finishModal.changeProfessional"
            ><fa-icon :icon="['fal', 'retweet']" /></button>
          </div>
        </div>
      </div>

      <div class="form-group">
        <h6 class="text-info">Lembrete de exame</h6>
        <div class="divider"></div>

        <div class="tile">
          <div class="tile-icon">
            <div class="avatar avatar-lg avatar-gray">
              <fa-icon :icon="['fal', 'calendar-star']" />
            </div>
          </div>
          <div class="tile-content">
            <div class="tile-title">Lembre-me deste exame</div>
            <div :class="{'has-error': $v.finishModal.form.reminderDate.$error}">
              <dx-input-date
                id="reminder-date"
                class="form-input text-center auto-width"
                v-model="finishModal.form.reminderDate"
                placeholder="00/00/0000"
                @blur="$v.finishModal.form.reminderDate.$touch()"
                :disabled="!finishModal.reminder"
              />
              <template v-if="$v.finishModal.form.reminderDate.$error">
                <div
                  class="form-input-hint"
                  v-if="!$v.finishModal.form.reminderDate.required"
                >Campo obrigatório</div>
                <div
                  class="form-input-hint"
                  v-else-if="!$v.finishModal.form.reminderDate.date"
                >Data inválida</div>
                <div
                  class="form-input-hint"
                  v-else-if="!$v.finishModal.form.reminderDate.minDate"
                >Data inválida</div>
              </template>
            </div>
          </div>
          <div class="tile-action text-right">
            <label class="form-switch no-label">
              <input type="checkbox" v-model="finishModal.reminder">
              <i class="form-icon"></i>
            </label>
          </div>
        </div>
      </div>

      <div class="form-group" v-if="canSetReviewer">
        <h6 class="text-info">Profissional revisor</h6>
        <div class="divider"></div>

        <div class="tile">
          <div class="tile-icon">
            <div class="avatar avatar-lg avatar-gray">
              <fa-icon :icon="['fal', 'clipboard-list-check']" />
            </div>
          </div>
          <div class="tile-content">
            <div class="tile-title">Enviar para revisão</div>
            <div :class="{'has-error': $v.finishModal.form.professionalReviewId.$error}">
              <select
                id="professional-reviewer"
                class="form-select auto-width"
                @blur="$v.finishModal.form.professionalReviewId.$touch()"
                v-model="finishModal.form.professionalReviewId"
                :disabled="!finishModal.reviewer"
              >
                <option value="">Selecione um profissional</option>
                <option
                  v-for="(item, i) in finishModal.professionals"
                  :value="item.id" :key="i"
                >{{ item.name }}</option>
              </select>
              <template v-if="$v.finishModal.form.professionalReviewId.$error">
                <div
                  class="form-input-hint"
                  v-if="!$v.finishModal.form.professionalReviewId.required"
                >Campo obrigatório</div>
              </template>
            </div>
          </div>
          <div class="tile-action text-right">
            <label class="form-switch no-label">
              <input
                type="checkbox"
                v-model="finishModal.reviewer"
                @change="toggleReviewer"
              ><i class="form-icon"></i>
            </label>
          </div>
        </div>
      </div>

      <div class="form-group">
        <h6 class="text-info">Assinatura digital</h6>
        <div class="divider" />

        <div class="tile">
          <div class="tile-icon">
            <div class="avatar avatar-lg avatar-gray">
              <fa-icon :icon="['fal', 'signature']" />
            </div>
          </div>
          <div class="tile-content">
            <div class="tile-title">Assinar digitalmente</div>
            <div class="tile-subtitle">
              Utilize suas credenciais <strong>Bird ID</strong> para assinar digitalmente
            </div>
          </div>
          <div class="tile-action text-right">
            <label class="form-switch no-label">
              <input
                type="checkbox"
                v-model="finishModal.digitalSignature"
                :disabled="!isDigitalSignatureEnabled || finishModal.reviewer"
              ><i class="form-icon"></i>
            </label>
          </div>
        </div>

      </div>

      <template v-slot:footer>
        <button
          v-if="finishModal.digitalSignature"
          class="btn btn-primary mr-2 btn-icon btn-icon-left"
          :class="{loading: saving}"
          :disabled="saving"
          @click="openDigitalSignature"
        >
          <fa-icon :icon="['fal', 'check-circle']" />
          <span>Finalizar e assinar</span>
        </button>
        <button
          v-else
          class="btn btn-primary mr-2 btn-icon btn-icon-left"
          :class="{loading: saving}"
          :disabled="saving"
          @click="save(true, false)"
        >
          <fa-icon :icon="['fal', finishModal.reviewer ? 'share' : 'check-circle']" />
          <span v-if="finishModal.reviewer">Enviar para revisão</span>
          <span v-else>Finalizar</span>
        </button>
        <button class="btn" @click="finishModal.show = false">Sair</button>
      </template>
    </dx-modal>

    <div class="bottom-bar" v-show="hasPortal">
      <portal-target
        name="report-bottom-bar"
        @change="hasPortal=$event"
      ></portal-target>
    </div>

    <patient-alert
      v-if="patientAlertModal.show"
      :patient-id="form.appointment.patient.id"
      :patient-name="form.appointment.patient.name"
      :external-data="patientAlertModal.data"
      :show="patientAlertModal.show"
      @close="patientAlertModal.show = false"
    />
  </div>
</template>

<script>
import formMixin from 'src/mixins/form';
import { mapState, mapGetters } from 'vuex';
import { mergeFrom } from '@/helpers/object';
import { required } from 'vuelidate/lib/validators';
import { date, minDate } from '@/data/validators';
import * as professionalApi from '@/app/professional/api';
import AblyService from '@/services/ably';
import { REASSIGN_MEDICAL_REPORT } from '@/data/actions/modules/clinical';
import ReportHeader from './report/Header.vue';
import ReportContent from './report/Content.vue';
import ReportSidebar from './report/Sidebar.vue';
import { normalizeImages } from './helpers';
import PatientAlert from '../../patient/modals/Alert.vue';

export default {
  mixins: [formMixin],
  components: {
    ReportHeader,
    ReportContent,
    ReportSidebar,
    PatientAlert,
  },
  data() {
    return {
      templates: [],
      form: this.blankForm(),
      loading: true,
      saving: false,
      finishModal: {
        show: false,
        professionals: [],
        changeProfessional: false,
        reviewer: false,
        reminder: false,
        digitalSignature: false,
        form: this.blankFinishModalForm(),
      },
      hasPortal: false,
      patientAlertModal: {
        show: false,
        data: null,
      },
    };
  },
  mounted() {
    this.init();
  },
  beforeDestroy() {
    this.stopEvents();
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    ...mapGetters({
      branchSettings: 'branchSettings',
    }),
    isFinished() {
      return this.form.stage === 'finished';
    },
    canSetReviewer() {
      return (
        this.form.stage === 'waiting'
        || this.form.stage === 'examined'
        || this.form.stage === 'report'
      );
    },
    canEdit() {
      if (this.isFinished) {
        return false;
      }
      if (['review', 'report'].includes(this.form.stage)) {
        const step = this.form.steps.find(({ status }) => status === this.form.stage);
        return step.professional.id === this.user.id;
      }

      return true;
    },
    canReSign() {
      return this.isFinished && this.canReassignAccess;
    },
    isExternal() {
      return !!this.form.externalReport;
    },
    canReassignAccess() {
      return this.$can(REASSIGN_MEDICAL_REPORT);
    },
    isDigitalSignatureEnabled() {
      return !!this.branchSettings?.digitalSignature.enabled;
    },
    digitalSignatureEventsChannel() {
      return `${this.user.branch.id}-digital-signature`;
    },
  },
  validations() {
    const rules = {
      finishModal: {
        form: {
          professionalId: { required },
          professionalReviewId: {},
          reminderDate: {},
        },
      },
    };

    if (this.finishModal.reminder) {
      rules.finishModal.form.reminderDate = { required, date, minDate: minDate() };
    }

    if (this.finishModal.reviewer) {
      rules.finishModal.form.professionalReviewId = { required };
    }

    return rules;
  },
  methods: {
    async init() {
      this.loading = true;
      try {
        await this.load(this.$route.params.id);
        this.startEvents();
        await this.loadAlert();
      } catch (e) {
        this.$toast.error(e);
      }
      this.loading = false;
    },
    async load(id) {
      const { data } = await this.$httpX.get(`/medical-reports/${id}`);
      data.images = normalizeImages(data.images);
      this.form = mergeFrom(this.blankForm(), data);
    },
    loadProfessionals() {
      return professionalApi.allActive()
        .then((data) => {
          this.finishModal.professionals = data.items;
        })
        .catch(this.$toast.error);
    },
    async loadAlert() {
      const params = {
        displayTo: 'medical_report',
      };

      const { data } = await this.$httpX
        .get(`/patients/${this.form.appointment.patient.id}/alerts`, { params });

      if (data && data.items && data.items.length > 0) {
        this.patientAlertModal.data = data;
        this.patientAlertModal.show = true;
      }
    },
    changeTemplate(template) {
      this.form.templateId = template.id;
      this.form.inputs = template.inputs;
      this.form.measures = template.measures;
    },
    changeMask(mask) {
      if (
        mask
        && this.$refs.content
        && Array.isArray(this.$refs.content.inputs)
        && this.$refs.content.inputs.length === 1
      ) {
        this.$refs.content.inputs[0].model.value = mask.data;
      }
    },
    toggleReviewer() {
      if (this.finishModal.reviewer) {
        this.finishModal.digitalSignature = false;
      }
    },
    setExternalReport(data) {
      this.form.externalReport = data && data.externalReport
        ? data.externalReport
        : null;
    },
    validate(isFinishing = false) {
      if (!this.canEdit) {
        return false;
      }

      if (isFinishing) {
        this.$v.finishModal.form.$touch();
        return !this.$v.finishModal.form.$error;
      }

      return true;
    },
    openDigitalSignature() {
      if (!this.validate(true)) return;
      this.$digitalSignature.open(() => this.save(true, true));
    },
    save(finish = false, digitalSignature = false) {
      if (!this.validate(finish)) return;

      this.saving = true;

      const {
        inputs = [],
        measures = [],
        templateId,
      } = this.$refs.content;

      const reportData = {
        stage: 'report',
        professionalId: finish ? this.finishModal.form.professionalId : this.user.id,
        patientId: this.form.appointment.patient.id,
        insurancePlanId: this.form.appointment.insurance.plan.id,
        templateId: templateId || this.form.templateId,
        inputs: inputs.map(({ id, model }) => ({ id, ...model })),
      };

      if (finish) {
        if (this.finishModal.reviewer) {
          reportData.stage = 'review';
          reportData.professionalReviewId = this.finishModal.form.professionalReviewId;
        } else {
          reportData.stage = 'finished';
          reportData.digitalSignature = digitalSignature;
        }

        if (this.finishModal.reminder && this.finishModal.form.reminderDate) {
          reportData.reminderDate = this.finishModal.form.reminderDate;
        }
      }

      if (Array.isArray(measures) && measures.length > 0) {
        reportData.measures = measures
          .reduce((a, { inputs: measureInputs }) => {
            measureInputs.forEach(({ type, id, value }) => {
              if (type !== 'formula') {
                a.push({ id, value });
              }
            });
            return a;
          }, []);
      }

      const request = !this.form.id
        ? this.$httpX.post('/medical-reports/', reportData)
        : this.$httpX.put(`/medical-reports/${this.form.id}`, reportData);

      request
        .then(({ data }) => {
          if (finish) {
            if (digitalSignature) {
              this.$digitalSignature.close();
            }
            this.finishModal.show = false;
            this.$toast.show('Laudo finalizado');
          } else {
            this.$toast.show('Informações salvas');
          }
          this.form.steps = data.steps;
          this.form.stage = data.stage;
          this.form.digitalSignature = data.digitalSignature;
        })
        .catch(this.$toast.error)
        .then(() => {
          this.saving = false;
        });
    },
    async sign() {
      this.clearFinishModal();
      this.finishModal.show = true;

      if (this.finishModal.professionals.length === 0) {
        await this.loadProfessionals();
      }

      const professional = this.finishModal.professionals
        .find(({ id }) => id === this.user.id);

      if (professional) {
        this.finishModal.form.professionalId = professional.id;
      }
    },
    async resign() {
      if (!this.canReassignAccess) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return;
      }
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente liberar este laudo para edição?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.saving = true;
              this.$httpX
                .post(`/medical-reports/${this.form.id}/resign`)
                .then(() => {
                  this.load(this.form.id);
                })
                .catch(() => {
                  this.$toast.show('Ocorreu um erro. Tente novamente!', { type: 'error' });
                })
                .then(() => {
                  this.saving = false;
                });
              close();
            },
          },
        ],
      });
    },
    digitalSignatureEventListener({ name, data }) {
      if (name === 'medical-report' && this.form.id === data.id) {
        this.form.digitalSignature = {
          ...this.form.digitalSignature,
          status: data.status,
        };
      }
    },
    startEvents() {
      const { realtime } = AblyService.instance(this.user.id);
      const channel = realtime.channels.get(this.digitalSignatureEventsChannel);
      channel.subscribe(this.digitalSignatureEventListener);
    },
    stopEvents() {
      const { realtime } = AblyService.instance(this.user.id);
      const channel = realtime.channels.get(this.digitalSignatureEventsChannel);
      channel.unsubscribe(this.digitalSignatureEventListener);
    },
    imageBlankForm() {
      return {
        file: null,
        preview: null,
        showOptions: false,
        showWebCam: false,
      };
    },
    clearFinishModal() {
      this.$v.finishModal.form.$reset();
      this.finishModal.reviewer = false;
      this.finishModal.reminder = false;
      this.finishModal.form = this.blankFinishModalForm();
    },
    blankFinishModalForm() {
      return {
        reminderDate: '',
        professionalId: '',
        professionalReviewId: '',
      };
    },
    blankForm() {
      return {
        id: '',
        stage: '',
        steps: [],
        appointment: {
          id: '',
          date: '',
          schedule: {
            id: '',
            name: '',
          },
          professional: {
            id: '',
            name: '',
          },
          insurance: {
            id: '',
            name: '',
            plan: {
              id: '',
              name: '',
            },
          },
          patient: {
            id: '',
            name: '',
            birthDate: '',
            gender: '',
            imageUrl: '',
          },
          expenses: [],
        },
        templateId: '',
        inputs: [],
        measures: [],
        dicomUrl: '',
        audios: [],
        images: [],
        files: [],
        externalReport: null,
        printLayout: null,
        digitalSignature: null,
      };
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";
.medical-report-page {
  $page-header-height: 3.7rem;

  .medical-report-header {
    background-color: $light-color;
    border-bottom: $border-width solid $border-color;
    height: $page-header-height;
    position: sticky;
    top: 0;
    z-index: $zindex-0 + 1;
  }
  .medical-report-content {
    display: flex;
    margin-left: $layout-spacing-lg;
    .mr-content {
      background-color: #fff;
      border-radius: $border-radius;
      flex: 1;
      padding: $layout-spacing-lg;
      overflow-x: auto;
      max-width: 42rem;
      margin: $layout-spacing-lg auto;
      .content-actions {
        margin-bottom: $layout-spacing;
        .btn {
          width: 7rem;
        }
      }
      .document-editor {
        overflow-x: hidden;
        .document-editor-body {
          .ck.ck-content.ck-editor__editable {
            min-height: 40rem;
            //padding: 15px 72px;
            padding: 15px;
          }
        }
      }
    }
    .mr-sidebar {
      // background-color: #eee;
      flex: 0 0 14rem;
      padding: $layout-spacing-lg;
    }
  }
  .bottom-bar {
    background-color: $light-color;
    border-top: $border-width solid $gray-color-light;
    bottom: 0;
    padding: $layout-spacing-lg;
    left: 110px;
    position: absolute;
    right: 0;
    .audio-media {
      width: 100%;
    }
  }
  .form-horizontal {
    .form-group {
      align-items: center;
    }
    .input-group-addon {
      width: 2.2rem;
      font-size: $font-size-xs;
      font-weight: 500;
    }
  }
}

#medical-report-modal-finish {
  .form-group {
    margin-bottom: $layout-spacing-lg;
  }
  .tile-content {
    margin: 0 $layout-spacing;
  }
  .avatar {
    color: $info-color;
  }
  .tile-title, .tile-subtitle {
    line-height: normal;
    margin-bottom: $layout-spacing-sm;
  }
  .tile-title {
    font-weight: 500;
  }
  .auto-width {
    width: auto;
    &.form-select {
      min-width: 12rem;
    }
  }

}
</style>
